/**
 * Cadencias publicitarias del handoff (§8), como funciones puras: son la lógica más fácil
 * de romper del sitio y la más barata de testear. Ninguna depende de la cantidad de bloques
 * ni de notas — son un módulo sobre el índice.
 */

/**
 * Índices de bloque **después** de los cuales va un intercalado (`IN_FEED`) en la home:
 * uno bajo la portada y uno cada 2 bloques de categoría. Última hora no cuenta en el
 * conteo (ya lleva su cadencia interna), y **nunca después del último bloque** — ahí ya
 * está el zócalo del pie, y dos avisos pegados es la saturación que el brief pide evitar.
 *
 * Más leídas tampoco cuenta, y no es un olvido: la cadencia que se pidió está escrita en bloques de
 * categoría, y sumar uno al conteo agregaría un aviso a la portada de todos los medios. Si se decide
 * que el ranking cuente, es una decisión de la cadencia y se cambia acá.
 */
export function intercaladosEntreBloques(bloques: { kind: string }[]): Set<number> {
  const indices = new Set<number>();
  let categorias = 0;
  bloques.forEach((bloque, i) => {
    const esUltimo = i === bloques.length - 1;
    if (esUltimo) return;
    if (bloque.kind === 'PORTADA') indices.add(i);
    if (bloque.kind === 'CATEGORIA') {
      categorias += 1;
      if (categorias % 2 === 0) indices.add(i);
    }
  });
  return indices;
}

/**
 * Índices de nota **después** de los cuales va un aviso dentro de una lista larga (Última
 * hora): uno cada `cada` notas, y la lista sigue abajo — si no sigue nada, no hay aviso
 * (una lista nunca termina en aviso).
 */
export function intercaladosEnLista(total: number, cada = 5): Set<number> {
  const indices = new Set<number>();
  for (let i = cada - 1; i < total - 1; i += cada) indices.add(i);
  return indices;
}

export interface Entrenotas {
  /** Índice de párrafo (base 1) después del cual va el primer entrenota, o `null`. */
  trasParrafo: number | null;
  /** Si va el segundo entrenota al final del cuerpo. */
  alFinal: boolean;
}

/**
 * Los dos espacios `IN_ARTICLE` de la nota: el primero después del 2º párrafo, el segundo
 * al final del cuerpo. Reglas duras (§8.4): nunca antes del 2º párrafo, ninguno en los
 * últimos 2, y al menos 3 párrafos entre dos avisos — por eso el primero solo existe si el
 * cuerpo tiene 5 párrafos o más. Un cuerpo de menos de 2 párrafos no lleva ninguno.
 */
export function entrenotas(nParrafos: number): Entrenotas {
  return {
    trasParrafo: nParrafos >= 5 ? 2 : null,
    alFinal: nParrafos >= 2,
  };
}
